import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Ban, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { supabase } from "@/integrations/supabase/client";
import { azError } from "@/lib/az-errors";
import { AUCTION_STATUS, fetchAllAuctions, formatAzn } from "@/lib/auctions";

export const Route = createFileRoute("/_authenticated/admin/herraclar")({
  component: Page,
});

const FILTERS = ["all", "active", "ended", "failed", "cancelled"] as const;

function Page() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("all");
  const { data = [], isLoading } = useQuery({ queryKey: ["auctions", "admin"], queryFn: fetchAllAuctions });
  const list = filter === "all" ? data : data.filter((a) => a.status === filter);

  const cancel = async (id: string) => {
    const { error } = await supabase.from("auctions").update({ status: "cancelled" }).eq("id", id);
    if (error) return void toast.error(azError(error));
    toast.success("Hərrac ləğv edildi");
    qc.invalidateQueries({ queryKey: ["auctions"] });
  };
  const remove = async (id: string) => {
    if (!confirm("Hərrac silinsin?")) return;
    const { error } = await supabase.from("auctions").delete().eq("id", id);
    if (error) return void toast.error(azError(error));
    toast.success("Hərrac silindi");
    qc.invalidateQueries({ queryKey: ["auctions"] });
  };

  return (
    <div>
      <AdminPageHeader title="Hərraclar" description="Bütün hərracları izləyin, ləğv edin və ya silin" />
      <div className="mb-3 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${filter === f ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}
          >
            {f === "all" ? "Hamısı" : AUCTION_STATUS[f]} ({f === "all" ? data.length : data.filter((a) => a.status === f).length})
          </button>
        ))}
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Yüklənir…</p>
      ) : list.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Hərrac yoxdur</p>
      ) : (
        <ul className="space-y-2">
          {list.map((a) => (
            <li key={a.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-2">
              {a.image && <img src={a.image} alt="" className="size-12 shrink-0 rounded-xl object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="line-clamp-1 text-sm font-semibold">{a.title}</p>
                <p className="text-xs text-muted-foreground">
                  {formatAzn(a.current_price)} · {a.bid_count} təklif · {AUCTION_STATUS[a.status] ?? a.status} · bitmə: {new Date(a.ends_at).toLocaleString("az-AZ")}
                </p>
              </div>
              {a.status === "active" && (
                <Button size="sm" variant="outline" onClick={() => cancel(a.id)} className="rounded-xl"><Ban className="size-4" /> Ləğv et</Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => remove(a.id)} aria-label="Sil"><Trash2 className="size-4 text-destructive" /></Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
